import { ListItemProps } from '@mui/material/ListItem';
import { useTheme, SxProps } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import { motion } from 'framer-motion';

import { TColorKeys } from 'theme/palette';
import { withSxProp } from 'common/utils/props';
import { ListItemButton } from 'common/components/ListItem/ListItemButton';

const listItemStyle: SxProps = {
    '&.MuiLink-underlineHover': {
        textDecoration: 'none',
    },

    '&.MuiListItem-button .MuiChip-root': {
        pointerEvents: 'none',
    },
};

interface IProps {
    // Button has been removed on the MuiListItem component so when user passes button prop,
    // we use our own ListItemButton component
    button?: boolean;
    disabled?: boolean;
    disableHover?: boolean;
    color?: TColorKeys;
    py?: number;
    px?: number;
    sx?: SxProps;
}

export function ListItem<C extends React.ElementType>({
    button,
    disabled,
    disableHover,
    color,
    py,
    px,
    sx,
    ...other
}: ListItemProps<C, { component?: C } & IProps>) {
    const theme = useTheme();
    const isSm = useMediaQuery(theme.breakpoints.up('sm'));

    const listItemStyles: SxProps = [
        {
            ...listItemStyle,
            p: {
                xs: `${theme.spacing(py !== undefined ? py : 1)} ${theme.spacing(px !== undefined ? px : 2)}`,
                sm: `${theme.spacing(py !== undefined ? py : 2)} ${theme.spacing(px !== undefined ? px : 4)}`,
            },

            cursor: disableHover ? 'default' : 'pointer',

            '&.Mui-focusVisible, &.MuiListItemButton-root.Mui-focusVisible, &:hover':
                {
                    ...(disableHover || !color
                        ? { backgroundColor: 'transparent' }
                        : {}),
                },
        },
        color && {
            color: `${color}.main`,
            backgroundColor: `${color}.bgColor`,
        },
        disabled && {
            pointerEvents: 'none',
        },
        ...withSxProp(sx),
    ];

    // Add animation when listitem is a button
    if (button && !disabled && isSm) {
        return (
            <motion.div
                initial="initListItemButton"
                whileHover="hoverListItemButton"
            >
                <ListItemButton sx={listItemStyles} {...other} />
            </motion.div>
        );
    }

    return (
        <Box>
            <ListItemButton
                disabled={disabled}
                sx={listItemStyles}
                {...other}
            />
        </Box>
    );
}
